import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import confetti from 'canvas-confetti';
import { sounds } from '../../utils/SoundManager';

const SymmetryAxisGame = () => {
    // Each shape has required 'axes' to find.
    // types: 'vertical', 'horizontal', 'diagonal1' (TL-BR), 'diagonal2' (TR-BL)
    const shapes = [
        {
            id: 0,
            name: "La Mariposa",
            description: "Tiene 1 eje de simetría",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-pink-500 fill-current drop-shadow-md">
                    <path d="M50 20 C60 5 90 5 90 30 C90 45 70 50 50 50 C70 50 90 60 90 85 C90 95 60 95 50 80 C40 95 10 95 10 85 C10 60 30 50 50 50 C30 50 10 45 10 30 C10 5 40 5 50 20 Z" />
                </svg>
            ),
            axes: ['vertical']
        },
        {
            id: 1,
            name: "El Rectángulo",
            description: "Tiene 2 ejes de simetría",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-blue-500 fill-current drop-shadow-md">
                    <rect x="15" y="30" width="70" height="40" rx="4" />
                </svg>
            ),
            axes: ['vertical', 'horizontal']
        },
        {
            id: 2,
            name: "El Cuadrado",
            description: "¡Tiene 4 ejes de simetría!",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-brand-purple fill-current drop-shadow-md">
                    <rect x="25" y="25" width="50" height="50" rx="4" />
                </svg>
            ),
            axes: ['vertical', 'horizontal', 'diagonal1', 'diagonal2']
        },
        {
            id: 3,
            name: "Letra A",
            description: "Tiene 1 eje de simetría",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-orange-500 fill-current drop-shadow-md">
                    <path d="M50 15 L80 85 L65 85 L58 65 L42 65 L35 85 L20 85 L50 15 Z M50 35 L45 55 L55 55 L50 35 Z" />
                </svg>
            ),
            axes: ['vertical']
        },
        {
            id: 4,
            name: "Letra M",
            description: "Tiene 1 eje de simetría",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-teal-500 fill-current drop-shadow-md">
                    <path d="M20 85 L20 25 L50 60 L80 25 L80 85 L65 85 L65 50 L50 70 L35 50 L35 85 Z" />
                </svg>
            ),
            axes: ['vertical']
        },
        {
            id: 5,
            name: "Letra H",
            description: "Tiene 2 ejes de simetría",
            svg: (
                <svg viewBox="0 0 100 100" className="w-full h-full text-indigo-500 fill-current drop-shadow-md">
                    <path d="M25 20 L40 20 L40 45 L60 45 L60 20 L75 20 L75 80 L60 80 L60 55 L40 55 L40 80 L25 80 Z" />
                </svg>
            ),
            axes: ['vertical', 'horizontal']
        },
        {
            id: 6,
            name: "El Búho",
            description: "Tiene 1 eje de simetría (Vertical)",
            svg: (
                <img src="/images/symmetry/owl.png" alt="Búho" className="w-full h-full object-cover rounded-xl" />
            ),
            axes: ['vertical']
        },
        {
            id: 7,
            name: "El Tigre",
            description: "Tiene 1 eje de simetría (Vertical)",
            svg: (
                <img src="/images/symmetry/tiger.png" alt="Tigre" className="w-full h-full object-cover rounded-xl" />
            ),
            axes: ['vertical']
        },
        {
            id: 8,
            name: "Copo de Nieve",
            description: "¡Múltiples ejes de simetría!",
            svg: (
                <img src="/images/symmetry/snowflake.png" alt="Copo de Nieve" className="w-full h-full object-cover rounded-xl" />
            ),
            // Snowflakes have hexagonal symmetry (3 main axes crossing center: vertical, diagonal1, diagonal2). 
            // Technically horizontal too if rotated, but let's stick to the 4 standard ones we implemented for simplicity:
            // Vertical, Horizontal, Diagonal1, Diagonal2
            axes: ['vertical', 'horizontal', 'diagonal1', 'diagonal2']
        }
    ];

    // State to track found axes for each shape: { 0: ['vertical'], 1: [] }
    const [foundAxes, setFoundAxes] = useState({});

    // Helper to check if a specific axis is found for a shape
    const isFound = (shapeId, axis) => foundAxes[shapeId]?.includes(axis);

    // Helper to check if shape is complete
    const isComplete = (shape) => {
        const found = foundAxes[shape.id] || [];
        return shape.axes.every(a => found.includes(a));
    };

    const handleAxisClick = (shape, axis) => {
        if (isFound(shape.id, axis)) return;
        if (!shape.axes.includes(axis)) {
            sounds.playIncorrect();
            return;
        }

        sounds.playPop();

        const currentFound = foundAxes[shape.id] || [];
        const newFound = [...currentFound, axis];

        setFoundAxes(prev => ({
            ...prev,
            [shape.id]: newFound
        }));

        if (shape.axes.every(a => newFound.includes(a))) {
            sounds.playCorrect();
            confetti({
                particleCount: 50,
                spread: 60,
                origin: { y: 0.6 }
            });
        }
    };

    // Helper to get hit zone styling (static CSS)
    const getHitZoneClass = (type) => {
        const base = "absolute cursor-pointer hover:bg-white/30 z-10 transition-colors";
        switch (type) {
            case 'vertical': return `${base} w-12 h-full left-1/2 -translate-x-1/2 top-0`;
            case 'horizontal': return `${base} h-12 w-full top-1/2 -translate-y-1/2 left-0`;
            case 'diagonal1': return `${base} w-12 h-[142%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45 origin-center`;
            case 'diagonal2': return `${base} w-12 h-[142%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 origin-center`;
            default: return '';
        }
    };

    // Helper to get motion line props
    // We strictly separate transform properties to the initial/animate object to avoid CSS conflict
    const getLineProps = (type) => {
        const baseClass = "absolute bg-red-500 pointer-events-none origin-center";

        switch (type) {
            case 'vertical':
                return {
                    className: `${baseClass} w-1 h-full left-1/2 top-0`,
                    initial: { x: '-50%', scaleY: 0, opacity: 0 },
                    animate: { x: '-50%', scaleY: 1, opacity: 1 }
                };
            case 'horizontal':
                return {
                    className: `${baseClass} h-1 w-full top-1/2 left-0`,
                    initial: { y: '-50%', scaleX: 0, opacity: 0 },
                    animate: { y: '-50%', scaleX: 1, opacity: 1 }
                };
            case 'diagonal1': // TL to BR (\) -> Rotate -45deg
                return {
                    className: `${baseClass} w-1 h-[142%] left-1/2 top-1/2`,
                    // Important: Reset x/y centering in initial/animate and apply rotation there
                    initial: { x: '-50%', y: '-50%', rotate: -45, scaleY: 0, opacity: 0 },
                    animate: { x: '-50%', y: '-50%', rotate: -45, scaleY: 1, opacity: 1 }
                };
            case 'diagonal2': // TR to BL (/) -> Rotate 45deg
                return {
                    className: `${baseClass} w-1 h-[142%] left-1/2 top-1/2`,
                    initial: { x: '-50%', y: '-50%', rotate: 45, scaleY: 0, opacity: 0 },
                    animate: { x: '-50%', y: '-50%', rotate: 45, scaleY: 1, opacity: 1 }
                };
            default: return {};
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {shapes.map((shape) => {
                const complete = isComplete(shape);
                const foundCount = (foundAxes[shape.id] || []).length;
                const totalCount = shape.axes.length;

                return (
                    <div key={shape.id} className="flex flex-col items-center gap-4">
                        <div
                            className="relative w-64 h-64 bg-white rounded-3xl border-4 border-gray-100 shadow-xl overflow-hidden group cursor-pointer"
                            onClick={() => {
                                if (!complete) sounds.playIncorrect();
                            }}
                        >
                            <div className="absolute inset-0 p-8 pointer-events-none z-0">
                                {shape.svg}
                            </div>

                            {/* Hit Zones */}
                            {['vertical', 'horizontal', 'diagonal1', 'diagonal2'].map(axisType => {
                                if (!shape.axes.includes(axisType) || isFound(shape.id, axisType)) return null;
                                return (
                                    <div
                                        key={`hit-${axisType}`}
                                        className={getHitZoneClass(axisType)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAxisClick(shape, axisType);
                                        }}
                                        title="Haz clic para marcar el eje"
                                    />
                                );
                            })}

                            {/* Found Lines */}
                            <AnimatePresence>
                                {shape.axes.map(axisType => {
                                    if (!isFound(shape.id, axisType)) return null;
                                    const props = getLineProps(axisType);
                                    return (
                                        <motion.div
                                            key={`line-${axisType}`}
                                            {...props}
                                        />
                                    );
                                })}
                            </AnimatePresence>

                            {/* Completion Badge */}
                            <AnimatePresence>
                                {complete && (
                                    <motion.div
                                        initial={{ scale: 0, rotate: -45 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        className="absolute top-2 right-2 bg-green-500 text-white p-2 rounded-full shadow-lg z-20"
                                    >
                                        <Check size={24} strokeWidth={4} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="text-center">
                            <h3 className="text-xl font-bold text-gray-700">{shape.name}</h3>
                            <p className="text-sm text-gray-400 mb-2">{shape.description}</p>
                            <div className="flex justify-center gap-1">
                                {Array.from({ length: totalCount }).map((_, i) => (
                                    <div
                                        key={i}
                                        className={`h-2 w-8 rounded-full transition-colors ${i < foundCount ? 'bg-green-500' : 'bg-gray-200'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default SymmetryAxisGame;
